import { ethers } from 'ethers';
import { CHAIN_ID } from './chain';
import { getContracts, type MethaContracts } from './contracts';
import { createStore } from './store';

export type WalletState =
  | { status: 'disconnected' }
  | { status: 'connecting' }
  | { status: 'wrong-network'; chainId: number }
  | {
      status: 'connected';
      address: string;
      provider: ethers.BrowserProvider;
      signer: ethers.Signer;
      contracts: MethaContracts;
    }
  | { status: 'error'; message: string };

const SEPOLIA_HEX = `0x${CHAIN_ID.toString(16)}`;

/* ---------- global store (single source of truth) ---------- */

export const walletStore = createStore<WalletState>({ status: 'disconnected' });

/* ---------- imperative actions ---------- */

/** Open MetaMask, request accounts, build signer + contracts. */
export async function connect(): Promise<void> {
  if (typeof window === 'undefined' || !window.ethereum) {
    walletStore.set({ status: 'error', message: 'MetaMask not detected' });
    return;
  }
  walletStore.set({ status: 'connecting' });
  try {
    const provider = new ethers.BrowserProvider(window.ethereum);
    await provider.send('eth_requestAccounts', []);
    const network = await provider.getNetwork();
    const chainId = Number(network.chainId);
    if (chainId !== CHAIN_ID) {
      walletStore.set({ status: 'wrong-network', chainId });
      return;
    }
    const signer = await provider.getSigner();
    const address = await signer.getAddress();
    const contracts = getContracts(signer);
    walletStore.set({ status: 'connected', address, provider, signer, contracts });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    walletStore.set({ status: 'error', message: msg });
  }
}

/**
 * Try to connect without prompting (eth_accounts returns prior allowance).
 * Used at app boot to restore session without a popup.
 */
export async function silentReconnect(): Promise<void> {
  if (typeof window === 'undefined' || !window.ethereum) {
    walletStore.set({ status: 'disconnected' });
    return;
  }
  try {
    const provider = new ethers.BrowserProvider(window.ethereum);
    const accounts = (await provider.send('eth_accounts', [])) as string[];
    if (!accounts || accounts.length === 0) {
      walletStore.set({ status: 'disconnected' });
      return;
    }
    const network = await provider.getNetwork();
    const chainId = Number(network.chainId);
    if (chainId !== CHAIN_ID) {
      walletStore.set({ status: 'wrong-network', chainId });
      return;
    }
    const signer = await provider.getSigner();
    const address = await signer.getAddress();
    const contracts = getContracts(signer);
    walletStore.set({ status: 'connected', address, provider, signer, contracts });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    walletStore.set({ status: 'error', message: msg });
  }
}

/** Local-only — clears our state. Wallet permission stays granted in MetaMask. */
export function disconnect(): void {
  walletStore.set({ status: 'disconnected' });
}

/** Ask MetaMask to switch to Sepolia. Mutates store on success. */
export async function switchToSepolia(): Promise<void> {
  if (typeof window === 'undefined' || !window.ethereum) return;
  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: SEPOLIA_HEX }],
    });
  } catch (e: any) {
    if (e?.code === 4902) {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: SEPOLIA_HEX,
          chainName: 'Sepolia',
          nativeCurrency: { name: 'SepoliaETH', symbol: 'ETH', decimals: 18 },
          rpcUrls: ['https://rpc.sepolia.org'],
          blockExplorerUrls: ['https://sepolia.etherscan.io'],
        }],
      });
    } else {
      throw e;
    }
  }
  await silentReconnect();
}

/* ---------- wallet event listeners (mounted once at boot) ---------- */

let listenersAttached = false;

/** Attach accountsChanged + chainChanged listeners. Idempotent — safe to call repeatedly. */
export function attachWalletListeners(): void {
  if (listenersAttached) return;
  if (typeof window === 'undefined' || !window.ethereum) return;
  const eth = window.ethereum;
  eth.on?.('accountsChanged', () => { silentReconnect(); });
  eth.on?.('chainChanged', () => { silentReconnect(); });
  listenersAttached = true;
}
